import React, { useState, useRef, useEffect } from "react";
import { useUser } from "../context/UserContext";
import { useSimulationProfileState } from "./hooks/useSimulationProfileState";
import PauseStatusBanner, {
  getSimulationStatusDisplay,
} from "./components/PauseStatusBanner";
import PauseModal from "./components/PauseModal";
import LoadingSpinner from "../common/LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faBrain,
  faClock,
  faEnvelope,
  faCalendarAlt,
  faComments,
  faArrowLeft,
  faLock,
  faSlidersH,
  faFlaskVial,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../../../i18n/LanguageContext";
import "../css/Common.css";
import "./css/SimulationProfileSettingsMobile.css";
import HelpGuideButton from "../HelpGuideButton";
import SpikySphere from "./components/SpikySphere";

function SimulationProfileSettingsMobile({ setHeroContent }) {
  const { isDemoSeat } = useUser();
  const {
    // State
    activeSection,
    setActiveSection,
    uuid,
    profileName,
    setProfileName,
    selectedPreset,
    setSelectedPreset,
    isEditable,
    existingProfile,
    loading,
    loadError,
    sophistication,
    setSophistication,
    frequency,
    setFrequency,
    communicationChannel,
    setCommunicationChannel,
    timeOfCommunication,
    setTimeOfCommunication,
    communicationContext,
    setCommunicationContext,

    // Pause state
    isPaused,
    hasActiveSchedule,
    profileStatus,
    pauseStartDate,
    pauseEndDate,
    pauseDurationDays,
    pauseReason,
    pausedAt,
    pausedBy,
    isPauseModalOpen,
    setIsPauseModalOpen,

    // Computed values
    canAccessCustomProfile,
    subscriptionLoading,
    subscriptionError,
    isGroupAdmin,
    selectedUser,
    sections,
    presets,
    presetDisplayName,

    // Event handlers
    handlePresetChange,
    handleSaveProfile,
    resetToOriginal,
    handlePause,
    handleResume,
    handlePauseConfirm,

    // Validation
    isProfileValid,
    hasChanges,
  } = useSimulationProfileState();

  const { t } = useTranslation();

  // Phase: "presets" = card selection, "dashboard" = review/tweak
  const scrollToConfig =
    sessionStorage.getItem("scrollToConfiguration") === "true";
  const [fromSetupJourney] = useState(
    () => sessionStorage.getItem("fromSetupJourney") === "true",
  );

  // Clear the flag so it doesn't persist on page refresh
  useEffect(() => {
    if (fromSetupJourney) {
      sessionStorage.removeItem("fromSetupJourney");
    }
  }, [fromSetupJourney]);
  const [phase, setPhase] = useState(
    scrollToConfig
      ? "presets"
      : existingProfile?.length > 0
        ? "dashboard"
        : "presets",
  );

  const configSectionRef = useRef(null);
  const [isStatusCollapsed, setIsStatusCollapsed] = useState(true);

  const simulationStatusDisplay = getSimulationStatusDisplay({
    isPaused,
    hasActiveSchedule,
    profileStatus,
    pauseEndDate,
    pauseDurationDays,
    isDemoMode: isDemoSeat,
    t,
  });

  useEffect(() => {
    if (scrollToConfig) {
      sessionStorage.removeItem("scrollToConfiguration");
      const timer = setTimeout(() => {
        configSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const capitalize = (str) => str.replace(/\b\w/g, (c) => c.toUpperCase());

  const presetCards = [
    {
      id: "Easy",
      icon: <SpikySphere level={0} size={48} />,
      title: t("sps.preset.easy.title"),
      subtitle: t("sps.preset.easy.subtitle"),
      description: t("sps.preset.easy.desc"),
      details: {
        difficulty: t("sps.preset.easy.difficulty"),
        frequency: t("sps.preset.easy.frequency"),
        channels: t("sps.preset.easy.channels"),
      },
    },
    {
      id: "Average",
      icon: <SpikySphere level={1} size={48} />,
      title: t("sps.preset.average.title"),
      subtitle: t("sps.preset.average.subtitle"),
      description: t("sps.preset.average.desc"),
      details: {
        difficulty: t("sps.preset.average.difficulty"),
        frequency: t("sps.preset.average.frequency"),
        channels: t("sps.preset.average.channels"),
      },
    },
    {
      id: "Difficult",
      icon: <SpikySphere level={2} size={48} />,
      title: t("sps.preset.difficult.title"),
      subtitle: t("sps.preset.difficult.subtitle"),
      description: t("sps.preset.difficult.desc"),
      details: {
        difficulty: t("sps.preset.difficult.difficulty"),
        frequency: t("sps.preset.difficult.frequency"),
        channels: t("sps.preset.difficult.channels"),
      },
    },
    {
      id: "Custom",
      icon: <SpikySphere level={3} size={48} />,
      title: t("sps.preset.custom.title"),
      subtitle: t("sps.preset.custom.subtitle"),
      description: t("sps.preset.custom.descMobile"),
      details: null,
      requiresPremium: true,
    },
  ];

  const handlePresetCardClick = (presetId) => {
    if (presetId === "Custom" && !canAccessCustomProfile) return;
    handlePresetChange({ target: { value: presetId } });
    setPhase("dashboard");
  };

  // All option definitions for inline badge display
  const allChannels = [
    { id: "email", label: t("sps.channel.email"), icon: "📧" },
    { id: "sms", label: t("sps.channel.sms"), icon: "📱" },
    { id: "qr", label: t("sps.channel.qr"), icon: "📱" },
    { id: "voice", label: t("sps.channel.voice"), icon: "📞" },
    { id: "social", label: t("sps.channel.social"), icon: "🌐" },
  ];

  const allTimings = [
    { id: "Day time", label: t("sps.timing.day"), icon: "☀️" },
    { id: "Evening", label: t("sps.timing.evening"), icon: "🌅" },
    { id: "Any time", label: t("sps.timing.any"), icon: "🕐" },
  ];

  const allContexts = [
    { id: "general", label: t("sps.context.general") },
    { id: "events", label: t("sps.context.events") },
    { id: "regional", label: t("sps.context.regional") },
    { id: "financial", label: t("sps.context.financial") },
    { id: "emergencies", label: t("sps.context.emergencies") },
    { id: "personalized", label: t("sps.context.personalized") },
  ];

  const sophisticationTiers = [
    { id: "basic", min: 1, max: 2, label: t("sps.tier.basic"), color: "#10b981" },
    { id: "intermediate", min: 3, max: 4, label: t("sps.tier.intermediate"), color: "#3b82f6" },
    { id: "advanced", min: 5, max: 6, label: t("sps.tier.advanced"), color: "#f59e0b" },
    { id: "expert", min: 7, max: 8, label: t("sps.tier.expert"), color: "#ef4444" },
    { id: "master", min: 9, max: 10, label: t("sps.tier.master"), color: "#7c3aed" },
  ];

  const frequencyTiers = [
    { id: "low", min: 1, max: 2, label: t("sps.freqTier.low"), color: "#10b981" },
    { id: "medium", min: 3, max: 5, label: t("sps.freqTier.medium"), color: "#3b82f6" },
    { id: "high", min: 6, max: 7, label: t("sps.freqTier.high"), color: "#f59e0b" },
    { id: "veryHigh", min: 8, max: 10, label: t("sps.freqTier.veryHigh"), color: "#ef4444" },
  ];

  if (loading || subscriptionLoading) {
    return <LoadingSpinner message={t("sps.loading")} />;
  }

  if (loadError || subscriptionError) {
    return (
      <div className="error-state">
        <p>{t("sps.error.prefix")}{loadError || subscriptionError}</p>
      </div>
    );
  }

  // Helpers to find the active tier
  const activeSophTier = sophisticationTiers.find(
    (tier) => sophistication >= tier.min && sophistication <= tier.max,
  );
  const activeFreqTier = frequencyTiers.find(
    (tier) => parseInt(frequency) >= tier.min && parseInt(frequency) <= tier.max,
  );

  return (
    <div className="common-container">
      <div className="common-header">
        <h1 className="main-title">{t("sps.page.title")}</h1>
        <div className="subtitle-with-help">
          <p className="common-subtitle">
            {t("sps.page.subtitle")}
          </p>
        </div>
        {selectedUser && (
          <div className="user-config-info">
            {t("sps.page.configuringFor").replace("{name}", `${selectedUser.firstName} ${selectedUser.lastName}`)}
          </div>
        )}
      </div>

      {/* ═══════ Section 1: Simulation Status ═══════ */}
      <div className="sps-section mobile">
        <h2
          className="sps-section-heading sps-section-heading-collapsible"
          role="button"
          tabIndex={0}
          aria-expanded={!isStatusCollapsed}
          aria-controls="sps-simulation-status-content-mobile"
          onClick={() => setIsStatusCollapsed((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsStatusCollapsed((v) => !v);
            }
          }}
        >
          <FontAwesomeIcon
            icon={isStatusCollapsed ? faChevronRight : faChevronDown}
            className="sps-section-heading-icon sps-section-toggle-icon"
          />
          {t("sps.status.heading")}
          <span
            className={`sps-status-header-badge ${simulationStatusDisplay.statusClass}`}
          >
            {simulationStatusDisplay.statusText}
          </span>
          <span
            className="sps-section-heading-helpguide"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <HelpGuideButton guideId={8} />
          </span>
        </h2>
        {!isStatusCollapsed && (
          <div id="sps-simulation-status-content-mobile">
            <PauseStatusBanner
              isPaused={isPaused}
              hasActiveSchedule={hasActiveSchedule}
              profileStatus={profileStatus}
              pauseStartDate={pauseStartDate}
              pauseEndDate={pauseEndDate}
              pauseDurationDays={pauseDurationDays}
              onPause={handlePause}
              onResume={handleResume}
              isDemoMode={isDemoSeat}
            />
          </div>
        )}
      </div>

      {/* ═══════ PHASE 1: Preset Cards ═══════ */}
      {phase === "presets" && (
        <div className="sps-section mobile" ref={configSectionRef}>
          <h2 className="sps-section-heading">
            <FontAwesomeIcon
              icon={faSlidersH}
              className="sps-section-heading-icon"
            />
            {t("sps.config.heading")}
            <HelpGuideButton guideId={14} />
          </h2>
          <div className="sps-presets-phase mobile">
            <h2 className="sps-presets-heading">{t("sps.presets.headingMobile")}</h2>
            <p className="sps-presets-subheading">
              {t("sps.presets.subheadingMobile")}
            </p>

            <div className="sps-preset-cards-grid mobile">
              {presetCards.map((card) => {
                const isDisabled =
                  card.requiresPremium && !canAccessCustomProfile;
                const isSelected = selectedPreset === card.id;

                return (
                  <div
                    key={card.id}
                    className={`sps-preset-card ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
                    onClick={() =>
                      !isDisabled && handlePresetCardClick(card.id)
                    }
                    role="button"
                    tabIndex={isDisabled ? -1 : 0}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                        e.preventDefault();
                        handlePresetCardClick(card.id);
                      }
                    }}
                  >
                    <div className="sps-preset-card-top">
                      <span className="sps-preset-card-emoji">{card.icon}</span>
                      <div>
                        <h3 className="sps-preset-card-title">{card.title}</h3>
                        <p className="sps-preset-card-subtitle">
                          {card.subtitle}
                        </p>
                      </div>
                      {isSelected && (
                        <div className="sps-preset-card-current-badge">
                          {t("sps.preset.currentBadgeMobile")}
                        </div>
                      )}
                    </div>
                    <p className="sps-preset-card-desc">{card.description}</p>

                    {card.details && (
                      <div className="sps-preset-card-details mobile">
                        <span>
                          <FontAwesomeIcon
                            icon={faBrain}
                            className="sps-detail-icon"
                          />
                          {card.details.difficulty}
                        </span>
                        <span>
                          <FontAwesomeIcon
                            icon={faClock}
                            className="sps-detail-icon"
                          />
                          {card.details.frequency}
                        </span>
                        <span>
                          <FontAwesomeIcon
                            icon={faEnvelope}
                            className="sps-detail-icon"
                          />
                          {card.details.channels}
                        </span>
                      </div>
                    )}

                    {isDisabled && (
                      <div className="sps-preset-card-locked">
                        <FontAwesomeIcon icon={faLock} />
                        <span>{t("sps.preset.locked")}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {existingProfile?.length > 0 && (
              <div className="sps-existing-profile-hint">
                <button
                  className="sps-link-button"
                  onClick={() => setPhase("dashboard")}
                >
                  {t("sps.preset.viewDetails")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════ PHASE 2: Dashboard ═══════ */}
      {phase === "dashboard" && (
        <>
          <button
            className="sps-back-nav-button mobile"
            onClick={() => {
              resetToOriginal();
              setPhase("presets");
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>{t("sps.dashboard.back")}</span>
          </button>
          <div className="sps-section mobile">
            <h2 className="sps-section-heading">
              <FontAwesomeIcon
                icon={faSlidersH}
                className="sps-section-heading-icon"
              />
              {t("sps.dashboard.heading")}
              <HelpGuideButton guideId={14} />
            </h2>
            <div className="sps-dashboard-phase mobile">
              <div className="sps-dashboard-header mobile">
                <div className="sps-current-preset-badge">
                  <span className="sps-preset-badge-emoji">
                    {presetCards.find((c) => c.id === selectedPreset)?.icon || (
                      <SpikySphere level={3} size={24} />
                    )}
                  </span>
                  <span className="sps-preset-badge-label">
                    {presetCards.find((c) => c.id === selectedPreset)?.title ||
                      selectedPreset}
                  </span>
                  {!isEditable && (
                    <span className="sps-preset-badge-tag">{t("sps.dashboard.tagPreset")}</span>
                  )}
                  {isEditable && (
                    <span className="sps-preset-badge-tag custom">{t("sps.dashboard.tagCustom")}</span>
                  )}
                  {existingProfile?.length > 0 && !hasChanges() && (
                    <span className="sps-active-badge">{t("sps.dashboard.tagActive")}</span>
                  )}
                </div>
              </div>

              {!isEditable && (
                <p className="sps-dashboard-hint mobile">
                  {t("sps.dashboard.hintPresetMobile").replace("{preset}", presetDisplayName)}{" "}
                  {canAccessCustomProfile && (
                    <button
                      className="sps-inline-link"
                      onClick={() => handlePresetCardClick("Custom")}
                    >
                      {t("sps.dashboard.switchToCustom")}
                    </button>
                  )}
                </p>
              )}

              {isEditable && (
                <p className="sps-dashboard-hint mobile">
                  {t("sps.dashboard.hintEditable")}
                </p>
              )}

              {/* Settings Content Card */}
              <div className="sps-settings-content mobile">
                {/* Inline Badge Dashboard */}
                <div className="sps-badge-dashboard mobile">
                  {/* Difficulty */}
                  <div className="sps-badge-row mobile">
                    <div className="sps-badge-row-label">
                      <FontAwesomeIcon
                        icon={faBrain}
                        className="sps-badge-row-icon"
                      />
                      <span>{t("sps.badge.difficulty")}</span>
                    </div>
                    <div className="sps-badge-row-values">
                      {sophisticationTiers.map((tier) => {
                        const isActive = activeSophTier === tier;
                        return (
                          <span
                            key={tier.id}
                            className={`sps-tier-badge ${isActive ? "active" : "inactive"} ${isEditable ? "editable" : ""}`}
                            style={
                              isActive
                                ? {
                                    background: `${tier.color}18`,
                                    color: tier.color,
                                    borderColor: tier.color,
                                  }
                                : {}
                            }
                            onClick={
                              isEditable
                                ? () =>
                                    setSophistication(
                                      Math.round((tier.min + tier.max) / 2),
                                    )
                                : undefined
                            }
                            role={isEditable ? "button" : undefined}
                            tabIndex={isEditable ? 0 : undefined}
                          >
                            {tier.label}
                            {isActive && (
                              <span className="sps-tier-value">
                                {sophistication}/10
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Frequency */}
                  <div className="sps-badge-row mobile">
                    <div className="sps-badge-row-label">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="sps-badge-row-icon"
                      />
                      <span>{t("sps.badge.frequency")}</span>
                    </div>
                    <div className="sps-badge-row-values">
                      {frequencyTiers.map((tier) => {
                        const isActive = activeFreqTier === tier;
                        return (
                          <span
                            key={tier.id}
                            className={`sps-tier-badge ${isActive ? "active" : "inactive"} ${isEditable ? "editable" : ""}`}
                            style={
                              isActive
                                ? {
                                    background: `${tier.color}18`,
                                    color: tier.color,
                                    borderColor: tier.color,
                                  }
                                : {}
                            }
                            onClick={
                              isEditable
                                ? () =>
                                    setFrequency(
                                      String(
                                        Math.round((tier.min + tier.max) / 2),
                                      ),
                                    )
                                : undefined
                            }
                            role={isEditable ? "button" : undefined}
                            tabIndex={isEditable ? 0 : undefined}
                          >
                            {tier.label}
                            {isActive && (
                              <span className="sps-tier-value">
                                {frequency}× / 10d
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Channels */}
                  <div className="sps-badge-row mobile">
                    <div className="sps-badge-row-label">
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className="sps-badge-row-icon"
                      />
                      <span>{t("sps.badge.channels")}</span>
                    </div>
                    <div className="sps-badge-row-values">
                      {allChannels.map((ch) => {
                        const isSelected = communicationChannel.includes(ch.id);
                        return (
                          <span
                            key={ch.id}
                            className={`sps-option-badge ${isSelected ? "active" : "inactive"} ${isEditable ? "editable" : ""}`}
                            onClick={
                              isEditable
                                ? () => {
                                    if (isSelected) {
                                      setCommunicationChannel(
                                        communicationChannel.filter(
                                          (c) => c !== ch.id,
                                        ),
                                      );
                                    } else {
                                      setCommunicationChannel([
                                        ...communicationChannel,
                                        ch.id,
                                      ]);
                                    }
                                  }
                                : undefined
                            }
                            role={isEditable ? "button" : undefined}
                            tabIndex={isEditable ? 0 : undefined}
                          >
                            <span className="sps-option-badge-icon">
                              {ch.icon}
                            </span>
                            {ch.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Timing */}
                  <div className="sps-badge-row mobile">
                    <div className="sps-badge-row-label">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="sps-badge-row-icon"
                      />
                      <span>{t("sps.badge.timing")}</span>
                    </div>
                    <div className="sps-badge-row-values">
                      {allTimings.map((timingItem) => (
                        <span
                          key={timingItem.id}
                          className={`sps-option-badge ${timeOfCommunication === timingItem.id ? "active" : "inactive"} ${isEditable ? "editable" : ""}`}
                          onClick={
                            isEditable
                              ? () => setTimeOfCommunication(timingItem.id)
                              : undefined
                          }
                          role={isEditable ? "button" : undefined}
                          tabIndex={isEditable ? 0 : undefined}
                        >
                          <span className="sps-option-badge-icon">
                            {timingItem.icon}
                          </span>
                          {timingItem.label}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Context */}
                  <div className="sps-badge-row mobile">
                    <div className="sps-badge-row-label">
                      <FontAwesomeIcon
                        icon={faComments}
                        className="sps-badge-row-icon"
                      />
                      <span>{t("sps.badge.context")}</span>
                    </div>
                    <div className="sps-badge-row-values">
                      {allContexts.map((ctx) => {
                        const isSelected = communicationContext.includes(
                          ctx.id,
                        );
                        return (
                          <span
                            key={ctx.id}
                            className={`sps-option-badge ${isSelected ? "active" : "inactive"} ${isEditable ? "editable" : ""}`}
                            onClick={
                              isEditable
                                ? () => {
                                    if (isSelected) {
                                      setCommunicationContext(
                                        communicationContext.filter(
                                          (c) => c !== ctx.id,
                                        ),
                                      );
                                    } else {
                                      setCommunicationContext([
                                        ...communicationContext,
                                        ctx.id,
                                      ]);
                                    }
                                  }
                                : undefined
                            }
                            role={isEditable ? "button" : undefined}
                            tabIndex={isEditable ? 0 : undefined}
                          >
                            {ctx.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="sps-dashboard-actions mobile">
                  <button
                    className={`common-button primary sps-save-btn ${
                      !isProfileValid() ? "disabled" : ""
                    }`}
                    onClick={handleSaveProfile}
                    disabled={!isProfileValid()}
                  >
                    {isEditable
                      ? t("sps.dashboard.saveCustom")
                      : t("sps.dashboard.savePreset").replace("{preset}", presetDisplayName)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Pause Modal */}
      <PauseModal
        isOpen={isPauseModalOpen}
        onClose={() => setIsPauseModalOpen(false)}
        onConfirm={handlePauseConfirm}
        currentPauseData={{
          pauseOption: pauseEndDate
            ? pauseDurationDays
              ? "duration"
              : "until-date"
            : "indefinite",
          endDate: pauseEndDate,
          durationDays: pauseDurationDays,
          reason: pauseReason,
        }}
      />

      {/* Back to Setup Journey link — shown when navigated from AccountSetupJourney */}
      {fromSetupJourney && setHeroContent && (
        <div className="setup-journey-return">
          <button
            className="setup-journey-return-link"
            onClick={() => {
              sessionStorage.setItem("expandSetupJourney", "true");
              setHeroContent("Dashboard");
            }}
          >
            <span className="return-arrow">←</span>
            {t("sps.setupJourney.continue")}
          </button>
        </div>
      )}
    </div>
  );
}

export default SimulationProfileSettingsMobile;
